import React, { FC, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { Button } from "../../../components/Button/Button";
import { InputPanel } from "../../../components/Brief/InputPanel";
import { BrandProject } from "./constants";
import { BRAND_PROJECT_STATUSES, describeSharedSiblings } from "../helper";
import { useProcessSiblings } from "../../../hooks/useProcessSiblings";
import { approveProject, cancelProject, projectStatusErrorMessage } from "./projectStatusActions";

interface Props {
    project: BrandProject;
    onClose: () => void;
    /** Fired after a successful decision. `approved` is false when not approved. */
    onComplete: (approved: boolean) => void;
}

const SummaryRow: FC<{ label: string; value?: string | null }> = ({ label, value }) => {
    if (!value) return null;
    return (
        <Box className="flex gap-4 items-start">
            <Typography className="!text-text-2 !font-circular !text-[14px] min-w-[110px] shrink-0">
                {label}
            </Typography>
            <Typography className="!text-text-1 !font-circular !text-[14px] !font-medium">
                {value}
            </Typography>
        </Box>
    );
};

/**
 * The internal team's approve / do-not-approve gate for a new project.
 *
 * Replaces hand-picking a status in the edit modal's Project Status dropdown:
 * Approve moves the project to Waiting For Brief, Not Approved moves it to
 * Cancelled with a required reason. Both statuses already carry their own
 * notifications on the API, so this dialog only has to pick one and say what
 * it will do.
 */
export const ProjectApprovalModal: FC<Props> = ({ project, onClose, onComplete }) => {
    const [confirmingDenial, setConfirmingDenial] = useState(false);
    const [submitting, setSubmitting] = useState<"approve" | "deny" | null>(null);

    // Approval is a SHARED transition: the API fans Waiting For Brief out to
    // every project on the same selection process that is still behind it, so
    // name them here rather than letting the approver discover it afterwards.
    const sharedProcess = useProcessSiblings(
        project?.order_id != null ? Number(project.order_id) : undefined,
        project?.id,
        { siblingCount: project?.process_project_count }
    );
    const alsoApproved = sharedProcess.siblingProjects.filter(
        (p) => Number(p.id) !== Number(project?.id) && p.status === BRAND_PROJECT_STATUSES.PENDING
    );

    const decide = async (approved: boolean, reason?: string) => {
        if (submitting) return;
        setSubmitting(approved ? "approve" : "deny");
        try {
            await (approved ? approveProject(project) : cancelProject(project, reason || ""));
            toast.success(
                approved
                    ? "Project approved — now waiting for the brief."
                    : "Project not approved — it has been cancelled."
            );
            // onComplete unmounts this dialog, so nothing may touch state after it.
            onComplete(approved);
            return;
        } catch (err) {
            toast.error(projectStatusErrorMessage(err));
        }
        // Reached only on failure: re-enable the buttons so the user can retry.
        setSubmitting(null);
    };

    // The list endpoint does not include the primary `product` association (it is
    // commented out in getAllBrandProjects), so match the primary by desireid
    // instead of trusting the order of the many-to-many `products` array — the
    // approver must see the product the project is actually for.
    const primaryProduct =
        project.product ||
        project.products?.find((p) => Number(p.desireid) === Number(project.desireid));
    const productTitle = primaryProduct?.title;
    const brandNames = (project.brands || [])
        .filter((b) => !b.rmn)
        .map((b) => b.name)
        .join(", ");
    const parentName = (project.brands || []).find((b) => b.rmn)?.name;

    if (confirmingDenial) {
        return (
            <Dialog open onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    <Typography variant="h5" className="!text-[20px]">
                        Not approve this project?
                    </Typography>
                </DialogTitle>
                <Formik initialValues={{ reason: "" }} onSubmit={(values) => decide(false, values.reason)}>
                    {({ values }) => (
                        <Form>
                            <DialogContent className="border-y border-border-token">
                                <Box className="flex flex-col gap-3 py-4">
                                    <Typography className="!text-text-2 !font-circular !text-[16px] !leading-[24px]">
                                        <b className="text-text-1">{project.title}</b> will be moved to
                                        Cancelled and the brand will be notified. Work on it stops here.
                                    </Typography>
                                    <Typography className="!text-text-2 !font-circular !text-[14px]">
                                        It can be restored later from the project menu.
                                    </Typography>
                                    <InputPanel
                                        name="reason"
                                        rows={3}
                                        body="Why isn't this project being approved? The brand will see this."
                                        placeholder="Reason"
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions className="!px-6 !py-4">
                                <Box className="flex items-center gap-6">
                                    <Button
                                        variant="default"
                                        disabled={!!submitting}
                                        onClick={() => setConfirmingDenial(false)}
                                        className="!bg-surface-2 hover:!bg-track !text-text-1 h-[40px] !py-0 !px-4 !min-w-fit"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={!!submitting || !values.reason.trim()}
                                        className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#EB3E33] hover:!bg-[#d82d21]"
                                    >
                                        {submitting === "deny" ? "Cancelling..." : "Yes, cancel project"}
                                    </Button>
                                </Box>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        );
    }

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h5" className="!text-[20px]">
                    Approve Project
                </Typography>
            </DialogTitle>
            <DialogContent className="border-y border-border-token">
                <Box className="flex flex-col gap-5 py-5">
                    <Box className="flex flex-col gap-2">
                        <SummaryRow label="Project" value={project.title} />
                        <SummaryRow label="Product" value={productTitle} />
                        <SummaryRow label="Brand" value={brandNames} />
                        <SummaryRow label="Parent" value={parentName} />
                        <SummaryRow label="Content type" value={project.content_type?.name} />
                        <SummaryRow label="Order" value={project.order?.name} />
                    </Box>

                    <Box className="flex flex-col gap-2 rounded-lg bg-surface-2 p-4">
                        <Typography className="!text-text-2 !font-circular !text-[14px] !leading-[20px]">
                            <b className="text-text-1">Approve</b> moves this project to{" "}
                            {BRAND_PROJECT_STATUSES.BRIEF_PENDING} so the brand can submit a brief.
                        </Typography>
                        <Typography className="!text-text-2 !font-circular !text-[14px] !leading-[20px]">
                            <b className="text-text-1">Not Approved</b> moves it to{" "}
                            {BRAND_PROJECT_STATUSES.CANCELLED}. Either way the brand is notified.
                        </Typography>
                    </Box>

                    {alsoApproved.length > 0 && (
                        <Typography className="!text-text-2 !font-circular !text-[14px] !leading-[20px]">
                            Approving also approves {describeSharedSiblings(alsoApproved)} — they share
                            this expert selection. Not approving affects this project only.
                        </Typography>
                    )}

                    {/* The notice above depends on a fetch. Say so rather than
                        letting its absence read as "this affects one project". */}
                    {sharedProcess.isLoading && (
                        <Typography className="!text-text-2 !font-circular !text-[14px]">
                            Checking whether other projects share this selection…
                        </Typography>
                    )}
                    {!!sharedProcess.error && (
                        <Typography className="!text-text-2 !font-circular !text-[14px]">
                            Couldn&apos;t check whether other projects share this selection. Approving
                            may also approve them.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions className="!px-6 !py-4">
                <Box className="flex items-center gap-6">
                    <Button
                        variant="contained"
                        disabled={!!submitting}
                        onClick={() => setConfirmingDenial(true)}
                        className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#EB3E33] hover:!bg-[#d82d21]"
                    >
                        Not Approved
                    </Button>
                    {/* Approve is the SHARED transition, so it waits for the sibling
                        check — a fast click must not fan the selection out before the
                        approver has been told it will. Not Approved never fans out, so
                        it stays enabled throughout. */}
                    <Button
                        variant="contained"
                        disabled={!!submitting || sharedProcess.isLoading}
                        onClick={() => decide(true)}
                        className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#1FCE7A] hover:!bg-[#45ae7b]"
                    >
                        {submitting === "approve" ? "Approving..." : "Approve"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
